import { useMemo, useState } from "react";
import { InputTable, InputColumnTableProps, InputTextCustom, Modal, ModalWidth } from "medere-ui-framework";
import { Button } from "primereact/button";
import { defaultMediaContent, MediaContent } from "../../../../../interface/interface";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";

interface Props {
    multimediaList: MediaContent[];
    onChange: (newMultimediaList: MediaContent[]) => void;
    onDelete: (multimediaElement: MediaContent, _idx: number) => void;
    modalWidth?: ModalWidth;
}

interface MediaErrors {
    description?: string;
    url?: string;
}

const createDefaultMediaContent = (): MediaContent => ({ ...defaultMediaContent });

export function MediaContentTable({ multimediaList, onChange, onDelete, modalWidth = "w-medium" }: Props) {
    const isMobile = useResizeListener(768);
    const [mediaItem, setMediaItem] = useState<MediaContent>(createDefaultMediaContent);
    const [mediaErrors, setMediaErrors] = useState<MediaErrors>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);

    const normalizedMultimedia = useMemo(() => multimediaList ?? [], [multimediaList]);

    const clearEditor = () => {
        setMediaItem(createDefaultMediaContent());
        setMediaErrors({});
        setEditIndex(null);
    };

    const closeMobileModal = () => {
        setMobileModalVisible(false);
        clearEditor();
    };

    const validateMedia = (media: MediaContent): MediaErrors => {
        const errors: MediaErrors = {};
        if (!(media.description ?? "").trim()) errors.description = "Este campo es requerido";
        if (!(media.source ?? "").trim()) errors.url = "Este campo es requerido";
        return errors;
    };

    const addOrUpdateMedia = async () => {
        const errors = validateMedia(mediaItem);
        setMediaErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) return false;

        const normalizedItem: MediaContent = {
            ...mediaItem,
            description: (mediaItem.description ?? "").trim(),
            source: (mediaItem.source ?? "").trim(),
        };

        if (editIndex === null) {
            onChange([...normalizedMultimedia, normalizedItem]);
        } else {
            onChange(normalizedMultimedia.map((item, idx) => idx === editIndex ? normalizedItem : item));
        }

        if (isMobile) {
            closeMobileModal();
        } else {
            clearEditor();
        }
        return true;
    };

    const handleEdit = (item: MediaContent, index: number, openModal = false) => {
        setMediaItem({
            ...item,
            description: item.description ?? "",
            source: item.source ?? "",
        });
        setMediaErrors({});
        setEditIndex(index);
        if (openModal) setMobileModalVisible(true);
    };

    const handleDelete = (item: MediaContent, index: number) => {
        onDelete(item, index);
        if (editIndex === null) return;
        if (editIndex === index) {
            clearEditor();
            return;
        }
        if (index < editIndex) setEditIndex(editIndex - 1);
    };

    const mediaCols: InputColumnTableProps<MediaContent>[] = [
        {
            header: "Descripción",
            className: "w-1/3",
            body: (m) => m.description,
            input: (
                <InputTextCustom
                    key={"inputvideonameid"}
                    className={(mediaErrors.description ? "" : "border2") + " w-full"}
                    value={mediaItem.description}
                    onChange={(e) => {
                        setMediaItem({ ...mediaItem, description: e.target.value });
                        setMediaErrors({ ...mediaErrors, description: "" });
                    }}
                    error={!!mediaErrors.description}
                    caption={mediaErrors.description}
                />
            )
        },
        {
            header: "Vínculo",
            className: "w-2/3 overflow-wrap-inside",
            body: (m) => m.source,
            input: (
                <InputTextCustom
                    key={"inputvideolinkid"}
                    className={(mediaErrors.url ? "" : "border2") + " w-full"}
                    value={mediaItem.source}
                    onChange={(e) => {
                        setMediaItem({ ...mediaItem, source: e.target.value });
                        setMediaErrors({ ...mediaErrors, url: "" });
                    }}
                    error={!!mediaErrors.url}
                    caption={mediaErrors.url}
                />
            )
        },
        {
            header: "",
            className: "",
            body: (_m, idx) => (
                <Button
                    icon="pi pi-trash"
                    className="p-button-text"
                    onClick={() => handleDelete(_m, idx)}
                />
            )
        },
    ];

    if (!isMobile) {
        return (
            <div>
                <InputTable
                    columnGap={4}
                    iconAddBtn="pi pi-plus-circle text-2xl"
                    headerDivClassName="font-semibold bg-bluegray-50"
                    dividerClassName="my-1 border-divider"
                    buttonColumnWidth={40}
                    items={normalizedMultimedia}
                    columns={mediaCols}
                    onAdd={addOrUpdateMedia}
                    divider
                    dividerInFirstItem
                />
            </div>
        );
    }

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    return (
        <div>
            <div className="location-mobile-inline-actions">
                <Button
                    className="location-mobile-add-btn"
                    icon="pi pi-plus-circle"
                    label="Agregar"
                    onClick={() => {
                        clearEditor();
                        setMobileModalVisible(true);
                    }}
                />
            </div>

            <div className="location-mobile-card-list">
                {normalizedMultimedia.map((item, index) => {
                    const menuItems: LocationMobileActionItem[] = [
                        { label: "Editar", icon: "pi pi-file-edit", action: () => handleEdit(item, index, true) },
                        { label: "Eliminar", icon: "pi pi-trash", action: () => handleDelete(item, index) },
                    ];

                    return (
                        <article key={`media-content-${item.item}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{formatCardValue(item.description, "Sin descripción")}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del contenido multimedia" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Vínculo</span>
                                <span className="location-mobile-card-value">{formatCardValue(item.source)}</span>
                            </div>
                        </article>
                    );
                })}
                {normalizedMultimedia.length === 0 && (
                    <p className="location-mobile-empty">No hay contenido multimedia asociado.</p>
                )}
            </div>

            <Modal
                className="location-mobile-editor-modal location-mobile-nested-editor-modal"
                visible={mobileModalVisible}
                setVisible={setMobileModalVisible}
                onHideCustom={closeMobileModal}
                width={modalWidth}
                header={<h1 className="info-dark text-2xl">{editIndex === null ? "Agregar contenido" : "Editar contenido"}</h1>}
            >
                <div className="location-config-view location-mobile-modal-content">
                    <div className="location-mobile-modal-fields">
                        <div>
                            <p>Descripción</p>
                            <InputTextCustom
                                className={(mediaErrors.description ? "" : "border2") + " w-full"}
                                value={mediaItem.description}
                                onChange={(e) => {
                                    setMediaItem({ ...mediaItem, description: e.target.value });
                                    setMediaErrors({ ...mediaErrors, description: "" });
                                }}
                                error={!!mediaErrors.description}
                                caption={mediaErrors.description}
                            />
                        </div>
                        <div>
                            <p>Vínculo</p>
                            <InputTextCustom
                                className={(mediaErrors.url ? "" : "border2") + " w-full"}
                                value={mediaItem.source}
                                onChange={(e) => {
                                    setMediaItem({ ...mediaItem, source: e.target.value });
                                    setMediaErrors({ ...mediaErrors, url: "" });
                                }}
                                error={!!mediaErrors.url}
                                caption={mediaErrors.url}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-end align-items-center gap-2 mt-2 location-mobile-modal-actions">
                        <Button label="Cancelar" onClick={closeMobileModal} className="btnCancel2 btnWidth" />
                        <Button label={editIndex === null ? "Agregar" : "Guardar"} onClick={() => { void addOrUpdateMedia(); }} className="btnContinue btnWidth" />
                    </div>
                </div>
            </Modal>
        </div>
    );
}


